import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { PageProps } from '../shared/defines';
import Button from '../shared/components/buttons';
import { WaiverContentToHtml } from '../components/waiver/content-to-html';
import { getWaiverContent } from '../components/waiver/waiver-content';
import { Input } from '../shared/components/input';
import { FingerDraw } from '../components/waiver/FingerDraw';
import { TextDecoder } from '../shared/text-effects/TextDecoder';
import {
  parentFirstNameState,
  parentLastNameState,
  parentSignatureDateState,
  parentSignatureState,
  venueIdState,
} from '../state/parent-details';

const WaiverSignaturePage: FC<PageProps> = ({ title }) => {
  const [venueId] = useRecoilState(venueIdState);
  const btnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const [firstName, setFirstName] = useRecoilState(parentFirstNameState);
  const [lastName, setLastName] = useRecoilState(parentLastNameState);
  const [signatureBase64, setSignatureBase64] = useState<string>('');
  const setSignature = useSetRecoilState(parentSignatureState);
  const setSignatureDate = useSetRecoilState(parentSignatureDateState);

  const handleEnter = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      btnRef.current?.click();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleEnter);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleEnter);
    };
  }, [handleEnter]);

  useEffect(() => {
    setSignature('');
    setSignatureDate('');
  }, [setSignature, setSignatureDate]);

  const onContinueButtonClick = () => {
    setSignature(signatureBase64);
    setSignatureDate(new Date().toISOString());
    navigate('/child-height');
  };

  useEffect(() => {
    document.title = title;
  }, [title]);

  const linkClassName =
    'underline underline-offset-3 underline-offset-2 text-green-laser hover:opacity-70 focus:opacity-50';

  const disabled = !firstName || !lastName || !signatureBase64;

  return (
    <div className="w-full flex-1 items-center flex">
      <div className="flex flex-col w-full">
        <div className="whitespace-normal w-full h-9 sm:h-13.5">
          <TextDecoder
            className="font-aeonik font-medium  text-[2.125rem]/[2.125rem] sm:text-[3.1rem]/[3.4rem] gap-5"
            delay={0}
            duration={500}
            animationType={'in'}
            text="Sign waiver"
          />
        </div>

        <div
          className="flex-1 text-left w-full my-10 text-white text-[1rem] sm:text-[1.2rem]"
          role="article"
        >
          <h2>
            SPYGAMES PARTICIPANT AGREEMENT, LIABILITY RELEASE, CLAIMS WAIVER,
            AND RISK ASSUMPTION AGREEMENT
          </h2>
          <p className="mt-5">
            I, the undersigned, hereby affirm my understanding and acceptance of
            the following conditions:
          </p>
          <ol className="list-decimal pl-5 leading-loose mt-10">
            <WaiverContentToHtml data={getWaiverContent(venueId)} />
            <li>
              I agree to the{' '}
              <a
                href="https://spyscape.com/purchase"
                className={linkClassName}
                target="_blank"
                rel="noreferrer"
              >
                Purchase
              </a>
              ,{' '}
              <a
                href="https://spyscape.com/privacy"
                className={linkClassName}
                target="_blank"
                rel="noreferrer"
              >
                Privacy
              </a>{' '}
              and{' '}
              <a
                href="https://spyscape.com/terms"
                className={linkClassName}
                target="_blank"
                rel="noreferrer"
              >
                Website
              </a>{' '}
              terms &amp; conditions.
            </li>
          </ol>

          <div className="mt-5">
            A hard copy of this agreement can also be signed at the SPYGAMES
            venue upon request, but this does not replace the necessity for the
            electronic agreement.
          </div>
          <div className="mt-5">
            <div>
              I have read this waiver &amp; agree to the terms set out therein.
            </div>
          </div>
        </div>

        <Input
          className="flex-1"
          size="sm"
          noLabel
          label="Parent/guardian first name"
          onChange={setFirstName}
          value={firstName}
        />

        <Input
          className="flex-1 mt-5 sm:mt-10"
          size="sm"
          noLabel
          label="Parent/guardian last name"
          onChange={setLastName}
          value={lastName}
        />

        <label className="block text-[1.2rem] font-aeonik font-medium mt-5 sm:mt-10 mb-1">
          Sign here
        </label>
        <FingerDraw onDataChanged={setSignatureBase64} />

        <Button
          ref={btnRef}
          variant="filled"
          disabled={disabled}
          size="sm"
          className="sm:mt-5 mt-0 text-[1.2rem] w-full sm:w-57.5"
          onClick={onContinueButtonClick}
        >
          I agree
        </Button>
      </div>
    </div>
  );
};

export default WaiverSignaturePage;
